import React, { useState, useMemo, useCallback } from 'react'
import { DeviceCategory, SignalType } from '@/types/devices'
import { useLayoutStore } from '@/stores/layoutStore'
import { useUIStore } from '@/stores/uiStore'
import { CATEGORY_COLORS, CATEGORY_LABELS } from '@/constants/categories'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import * as Dialog from '@radix-ui/react-dialog'
import {
  FaTimes as X,
  FaSearch as Search,
  FaPlus as Plus,
  FaCamera as Camera,
  FaCheckCircle as CheckCircle,
} from 'react-icons/fa'

// Import device presets from shared package
import { DEVICE_PRESETS } from '@busswerks/shared'
import type { DevicePreset } from '@busswerks/shared'
import { presetToDevice, gridPosition } from '@/lib/presetToDevice'
import { PhotoAddDeviceDialog } from './PhotoAddDeviceDialog'
import { EditDeviceDialog } from './EditDeviceDialog'
import type { Device } from '@/types/devices'

interface PresetWithCount extends DevicePreset {
  inputCount: number
  outputCount: number
  portCounts: Record<string, number>
}

interface AddDeviceDialogProps {
  isOpen: boolean
  onClose: () => void
}

const categoryOrder: DeviceCategory[] = [
  'interface',
  'mixer',
  'synth',
  'drum_machine',
  'sampler',
  'controller',
  'effects',
  'monitoring',
  'midi_interface',
  'groovebox',
  'semi_modular',
  'computer',
  'usb_hub',
  'network_switch',
  'recorder',
  'microphone',
]

export const AddDeviceDialog: React.FC<AddDeviceDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<
    DeviceCategory | 'all'
  >('all')
  const [addedThisSession, setAddedThisSession] = useState(0)
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false)
  const [customDeviceDialogOpen, setCustomDeviceDialogOpen] = useState(false)
  // Phase 4: verified-only is default-on so users don't see unreviewed
  // contributed presets unless they opt in. Missing `verified` fields
  // on built-in presets are treated as verified (see inline default
  // in the filter below).
  const [verifiedOnly, setVerifiedOnly] = useState(true)

  const addDevice = useLayoutStore(state => state.addDevice)
  const devices = useLayoutStore(state => state.devices)
  const focusedZoneId = useUIStore(state => state.focusedZoneId)

  // Count how many instances of each preset are already in the layout,
  // keyed by "manufacturer|name" so we can show an "In layout: N" badge.
  const inLayoutCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const device of devices) {
      const key = `${device.manufacturer ?? ''}|${device.name}`
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return counts
  }, [devices])

  // Prepare presets with counts
  const presetsWithCounts: PresetWithCount[] = useMemo(
    () =>
      DEVICE_PRESETS.map(preset => {
        const portCounts: Record<string, number> = {}
        ;[...preset.inputs, ...preset.outputs].forEach(port => {
          const type = port.signalType
          portCounts[type] = (portCounts[type] || 0) + 1
        })
        return {
          ...preset,
          inputCount: preset.inputs.length,
          outputCount: preset.outputs.length,
          portCounts,
        }
      }),
    [],
  )

  // Filter presets by search, category, and verified status.
  // Presets that don't explicitly set `verified` are treated as
  // verified — every entry in the built-in catalog is hand-curated
  // and matches real hardware, so the default means "trust it".
  const filteredPresets = useMemo(() => {
    return presetsWithCounts.filter(preset => {
      const matchesSearch =
        searchQuery === '' ||
        preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preset.manufacturer
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        preset.type.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesCategory =
        selectedCategory === 'all' || preset.category === selectedCategory

      const isVerified = preset.verified ?? true
      const matchesVerified = !verifiedOnly || isVerified

      return matchesSearch && matchesCategory && matchesVerified
    })
  }, [searchQuery, selectedCategory, presetsWithCounts, verifiedOnly])

  // Group by category
  const groupedPresets = useMemo(() => {
    const groups: Partial<Record<DeviceCategory, PresetWithCount[]>> = {}
    filteredPresets.forEach(preset => {
      if (!groups[preset.category]) {
        groups[preset.category] = []
      }
      groups[preset.category]!.push(preset)
    })
    return groups
  }, [filteredPresets])

  const handleAddDevice = useCallback(
    (preset: PresetWithCount) => {
      const newDevice = presetToDevice(preset, {
        position: gridPosition(devices.length + addedThisSession),
        // When a zone is drilled into, new devices land there.
        // Otherwise they're unassigned (zoneId: undefined) and render
        // at the layout level.
        zoneId: focusedZoneId ?? undefined,
      })
      addDevice(newDevice)
      setAddedThisSession(c => c + 1)
    },
    [devices.length, addedThisSession, addDevice, focusedZoneId],
  )

  const handleDone = useCallback(() => {
    setAddedThisSession(0)
    onClose()
  }, [onClose])

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleDone()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[80vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col border border-brd-subtle">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <Dialog.Title className="text-xl font-semibold text-content-primary">
              Add Device
            </Dialog.Title>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCustomDeviceDialogOpen(true)}
                className="btn btn-sm bg-surface-sunken text-content-primary border border-brd-subtle hover:border-brd-emphasis hover:bg-surface-overlay"
                title="Build a custom device from scratch — name it, pick a category, and define its ports"
              >
                <Plus className="w-3.5 h-3.5" />
                Custom device
              </button>
              <button
                type="button"
                onClick={() => setPhotoDialogOpen(true)}
                className="btn-accent-gradient btn-sm font-semibold"
                title="Identify a device from a photo using Claude vision"
              >
                <Camera className="w-3.5 h-3.5" />
                From photo
              </button>
              <Dialog.Close asChild>
                <button className="p-1.5 hover:bg-surface-sunken rounded-md transition-colors duration-150">
                  <X className="w-5 h-5 text-content-tertiary" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search Bar + verified-only toggle */}
            <div className="px-6 py-4 border-b border-brd-subtle flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-content-tertiary" />
                <input
                  type="text"
                  placeholder="Search devices..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-brd-subtle rounded-lg bg-surface-sunken text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
                  autoFocus
                />
              </div>
              <label
                className="flex items-center gap-1.5 text-xs text-content-secondary cursor-pointer select-none whitespace-nowrap"
                title="Hide user-contributed presets that haven't been verified against real hardware"
              >
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={e => setVerifiedOnly(e.target.checked)}
                  className="rounded border-brd-subtle text-accent focus:ring-accent/50"
                />
                <CheckCircle className="w-3 h-3 text-success" />
                Verified only
              </label>
            </div>

            {/* Category Filter Pills */}
            <div className="px-6 py-3 border-b border-brd-subtle">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors duration-150 ${
                    selectedCategory === 'all'
                      ? 'bg-accent text-white'
                      : 'bg-surface-sunken text-content-secondary hover:text-content-primary hover:bg-surface-overlay'
                  }`}
                >
                  All
                </button>
                {categoryOrder.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors duration-150 flex items-center gap-1.5 ${
                      selectedCategory === cat
                        ? 'text-white'
                        : 'bg-surface-sunken text-content-secondary hover:text-content-primary hover:bg-surface-overlay'
                    }`}
                    style={
                      selectedCategory === cat
                        ? { backgroundColor: CATEGORY_COLORS[cat] }
                        : undefined
                    }
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: CATEGORY_COLORS[cat],
                      }}
                    />
                    {CATEGORY_LABELS[cat]}
                  </button>
                ))}
              </div>
            </div>

            {/* Device List */}
            <div className="flex-1 overflow-y-auto">
              {Object.keys(groupedPresets).length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-content-tertiary">No devices found</p>
                </div>
              ) : (
                <div className="divide-y divide-brd-subtle">
                  {categoryOrder.map(category => {
                    const presets = groupedPresets[category]
                    if (!presets || presets.length === 0) return null

                    return (
                      <div key={category}>
                        <div className="px-6 py-3 bg-surface-sunken sticky top-0 z-10">
                          <h3 className="text-sm font-semibold text-content-secondary flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor: CATEGORY_COLORS[category],
                              }}
                            />
                            {CATEGORY_LABELS[category]}
                          </h3>
                        </div>
                        {presets.map(preset => {
                          const inLayoutCount =
                            inLayoutCounts.get(
                              `${preset.manufacturer ?? ''}|${preset.name}`,
                            ) ?? 0
                          return (
                            <div
                              key={`${preset.name}-${preset.manufacturer}`}
                              className="group flex items-start gap-4 px-6 py-4 border-l-4 border-transparent hover:bg-accent-muted transition-colors duration-150"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-medium text-content-primary">
                                    {preset.name}
                                  </h4>
                                  {(preset.verified ?? true) && (
                                    <CheckCircle
                                      className="w-3.5 h-3.5 text-success flex-shrink-0"
                                      title="Verified preset — hand-checked against real hardware"
                                    />
                                  )}
                                  {preset.source === 'contributed' &&
                                    preset.contributorId && (
                                      <span
                                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[color:var(--second-soft)] text-second"
                                        title={`Contributed by ${preset.contributorId}`}
                                      >
                                        Contributed
                                      </span>
                                    )}
                                  {inLayoutCount > 0 && (
                                    <span
                                      className="px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-muted text-accent"
                                      title={`${inLayoutCount} already in this layout`}
                                    >
                                      {inLayoutCount} in layout
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-content-secondary">
                                  {preset.manufacturer} • {preset.type}
                                </p>

                                {/* Port Summary */}
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {Object.entries(preset.portCounts)
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([signalType, count]) => (
                                      <span
                                        key={signalType}
                                        className="px-2 py-1 rounded text-xs font-medium text-white"
                                        style={{
                                          backgroundColor:
                                            SIGNAL_TYPE_COLORS[
                                              signalType as SignalType
                                            ] || '#999',
                                        }}
                                      >
                                        {count}x{' '}
                                        {signalType
                                          .replace(/_/g, ' ')
                                          .toUpperCase()}
                                      </span>
                                    ))}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleAddDevice(preset)}
                                className="flex-shrink-0 btn btn-sm bg-accent text-white hover:bg-accent-hover opacity-70 group-hover:opacity-100"
                                title={`Add ${preset.name} to the canvas`}
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-brd-subtle flex items-center justify-between bg-surface-sunken">
            <span className="text-sm text-content-secondary">
              {addedThisSession === 0
                ? 'Click Add on any device to place it on the canvas.'
                : `Added ${addedThisSession} device${addedThisSession === 1 ? '' : 's'} this session.`}
            </span>
            <button
              type="button"
              onClick={handleDone}
              className="btn-primary btn-md font-medium"
            >
              Done
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
      <PhotoAddDeviceDialog
        isOpen={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
      />
      <EditDeviceDialog
        isOpen={customDeviceDialogOpen}
        onClose={() => setCustomDeviceDialogOpen(false)}
        device={null}
        onCreate={(d: Device) => {
          addDevice({
            ...d,
            position: gridPosition(devices.length + addedThisSession),
            zoneId: focusedZoneId ?? undefined,
          })
          setAddedThisSession(c => c + 1)
          setCustomDeviceDialogOpen(false)
        }}
      />
    </Dialog.Root>
  )
}
